import React, { useEffect, useState, useCallback } from "react";
import { DownloadCloud, RefreshCw, Loader2, CheckCircle2, XCircle, Clock, CalendarClock } from "lucide-react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const map = { completed: ["bg-emerald-50 text-emerald-700 border-emerald-200", CheckCircle2], running: ["bg-amber-50 text-amber-700 border-amber-200", Loader2], failed: ["bg-red-50 text-red-700 border-red-200", XCircle] };
  const [cls, Icon] = map[status] || map.failed;
  return <span className={`inline-flex items-center gap-1 text-xs font-medium border rounded px-2 py-0.5 ${cls}`}><Icon className={`w-3 h-3 ${status === "running" ? "animate-spin" : ""}`} /> {status}</span>;
};

const LogTable = ({ logs }) => (
  <div className="border border-border rounded-lg overflow-hidden bg-white">
    <table className="w-full text-sm">
      <thead className="bg-surface text-steel-light">
        <tr className="text-left mono text-xs uppercase tracking-wider">
          <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Added</th><th className="px-4 py-2.5">Updated</th><th className="px-4 py-2.5">Unchanged</th><th className="px-4 py-2.5">Cats/Brands</th><th className="px-4 py-2.5">Started</th>
        </tr>
      </thead>
      <tbody>
        {logs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-steel-light">No runs yet.</td></tr>}
        {logs.map((l) => (
          <tr key={l.id} data-testid={`log-row-${l.id}`} className="border-t border-border">
            <td className="px-4 py-3"><StatusBadge status={l.status} />{l.error && <div className="text-xs text-red-600 mt-1 max-w-xs truncate">{l.error}</div>}</td>
            <td className="px-4 py-3 mono text-emerald-600">{l.added}</td>
            <td className="px-4 py-3 mono text-orange">{l.updated}</td>
            <td className="px-4 py-3 mono text-steel-light">{l.unchanged}</td>
            <td className="px-4 py-3 mono text-steel">{l.categories}/{l.brands}</td>
            <td className="px-4 py-3 mono text-xs text-steel-light">{l.started_at ? new Date(l.started_at).toLocaleString() : ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ImportSync = () => {
  const [importLogs, setImportLogs] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    client.get("/admin/import-logs").then((r) => setImportLogs(r.data.items));
    client.get("/admin/sync-logs").then((r) => setSyncLogs(r.data.items));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);

  const run = async (type) => {
    setBusy(type);
    try {
      await client.post(`/admin/${type}`, null, { params: type === "import" ? { limit: 150 } : { limit: 300 } });
      toast.success(`${type === "import" ? "Import" : "Sync"} started`, { description: "Reading public catalogue from gotparts.ca…" });
      setTimeout(load, 1500);
    } catch { toast.error("Failed to start"); }
    finally { setTimeout(() => setBusy(""), 2000); }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-graphite">Catalogue Import & Sync</h1>
      <p className="text-sm text-steel-light mt-1 mb-6">Crawl the public GOT PARTS catalogue. SKU is the primary key — products are never duplicated.</p>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="bg-white border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-1"><DownloadCloud className="w-5 h-5 text-orange" /><span className="font-semibold text-graphite">Import Catalogue</span></div>
          <p className="text-sm text-steel-light mb-4">Discover and import products, categories and brands from the source website.</p>
          <Button data-testid="run-import" onClick={() => run("import")} disabled={!!busy} className="w-full h-11 bg-orange hover:bg-orange-hover">
            {busy === "import" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DownloadCloud className="w-4 h-4 mr-2" />} Run Import
          </Button>
        </div>
        <div className="bg-white border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-1"><RefreshCw className="w-5 h-5 text-orange" /><span className="font-semibold text-graphite">Sync Catalogue</span></div>
          <p className="text-sm text-steel-light mb-4">Detect new products, update descriptions, images, stock, prices & attributes.</p>
          <Button data-testid="run-sync" onClick={() => run("sync")} disabled={!!busy} variant="outline" className="w-full h-11 border-graphite">
            {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />} Run Sync
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 text-sm text-steel-light bg-surface border border-border rounded-md px-4 py-2.5">
        <CalendarClock className="w-4 h-4 text-orange" /> Scheduled automatic sync is supported and planned (daily) — run manually for now.
      </div>

      <Tabs defaultValue="import">
        <TabsList>
          <TabsTrigger value="import" data-testid="tab-import-logs">Import Logs</TabsTrigger>
          <TabsTrigger value="sync" data-testid="tab-sync-logs">Sync Logs</TabsTrigger>
        </TabsList>
        <TabsContent value="import" className="mt-4"><LogTable logs={importLogs} /></TabsContent>
        <TabsContent value="sync" className="mt-4"><LogTable logs={syncLogs} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default ImportSync;
